import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  listBiometricRecords,
  getBiometricSummary,
  getBiometricOptions,
  COMPANY_OPTIONS,
  BRANCH_OPTIONS,
  DEVICE_OPTIONS,
  DATE_PERIOD_OPTIONS,
  SEARCH_FIELD_OPTIONS,
} from '../api/biometricRecordsApi'

const DEFAULT_PAGE_SIZE = 25

export function useBiometricRecordsListScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('studentId')
  const fromStudent = searchParams.get('from') === 'students'

  const [rows, setRows] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [summary, setSummary] = useState({
    totalSwipes: 0,
    uniqueStudents: 0,
    activeDevices: 0,
    todaySwipes: 0,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  const [searchField, setSearchField] = useState('All')
  const [searchText, setSearchText] = useState('')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [batchFilter, setBatchFilter] = useState('All')
  const [deviceFilter, setDeviceFilter] = useState('All')
  const [datePeriod, setDatePeriod] = useState('period')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [orderBy, setOrderBy] = useState(1)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const [options, setOptions] = useState({
    companyOptions: COMPANY_OPTIONS,
    branchOptions: BRANCH_OPTIONS,
    deviceOptions: DEVICE_OPTIONS,
    courses: [],
    batches: [],
    datePeriodOptions: DATE_PERIOD_OPTIONS,
    searchFieldOptions: SEARCH_FIELD_OPTIONS,
  })

  // Load options on mount
  useEffect(() => {
    let isMounted = true
    getBiometricOptions()
      .then((opts) => {
        if (isMounted) {
          setOptions({
            companyOptions: opts.companyOptions || COMPANY_OPTIONS,
            branchOptions: opts.branchOptions || BRANCH_OPTIONS,
            deviceOptions: opts.deviceOptions || DEVICE_OPTIONS,
            courses: opts.courses || [],
            batches: opts.batches || [],
            datePeriodOptions: DATE_PERIOD_OPTIONS,
            searchFieldOptions: SEARCH_FIELD_OPTIONS,
          })
        }
      })
      .catch(() => {})
    return () => {
      isMounted = false
    }
  }, [])

  // Load live summary KPI metrics
  const loadSummary = useCallback(async () => {
    try {
      const sum = await getBiometricSummary({ fromDate, toDate })
      setSummary(sum)
    } catch {
      // Keep previous summary
    }
  }, [fromDate, toDate])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const toISODate = (date) => {
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handleDatePeriodChange = (preset) => {
    setDatePeriod(preset)
    if (preset === 'period') {
      setFromDate('')
      setToDate('')
      return
    }

    const now = new Date()
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const sunday = new Date(startOfDay)
    sunday.setDate(startOfDay.getDate() - ((startOfDay.getDay() + 6) % 7))

    const dayOffset = (days) => new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + days)

    const ranges = {
      today: [startOfDay, startOfDay],
      yesterday: [new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1), new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)],
      current_week: [dayOffset(0), dayOffset(6)],
      current_month: [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0)],
      current_year: [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31)],
      last_week: [dayOffset(-7), dayOffset(-1)],
      last_month: [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)],
      last_year: [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)],
    }

    const range = ranges[preset]
    if (range) {
      setFromDate(toISODate(range[0]))
      setToDate(toISODate(range[1]))
    }
  }

  const queryParams = useMemo(
    () => ({
      company: companyFilter,
      branch: branchFilter,
      course: courseFilter,
      batch: batchFilter,
      deviceId: deviceFilter,
      period: datePeriod,
      fromDate,
      toDate,
      studentId: studentId ? Number(studentId) : undefined,
      searchField,
      searchText,
      orderBy,
      page: currentPage,
      pageSize,
    }),
    [
      companyFilter,
      branchFilter,
      courseFilter,
      batchFilter,
      deviceFilter,
      datePeriod,
      fromDate,
      toDate,
      studentId,
      searchField,
      searchText,
      orderBy,
      currentPage,
      pageSize,
    ]
  )

  const loadBiometricRecords = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const res = await listBiometricRecords(queryParams)
      setRows(res.data || [])
      setTotalCount(res.total || 0)
      setTotalPages(res.totalPages || Math.max(1, Math.ceil((res.total || 0) / pageSize)))
    } catch {
      setError('Failed to load biometric records. Please try again.')
      setRows([])
      setTotalCount(0)
      setTotalPages(1)
    } finally {
      setIsLoading(false)
    }
  }, [queryParams, pageSize])

  useEffect(() => {
    loadBiometricRecords()
  }, [currentPage, pageSize, loadBiometricRecords])

  const resetFilters = () => {
    setSearchField('All')
    setSearchText('')
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setBatchFilter('All')
    setDeviceFilter('All')
    setDatePeriod('period')
    setFromDate('')
    setToDate('')
    setOrderBy(1)
    setCurrentPage(1)
    setTimeout(() => {
      loadBiometricRecords()
      loadSummary()
    }, 10)
  }

  const goBack = () => {
    if (fromStudent) {
      navigate('/admission/students/list')
    }
  }

  // Format options for select inputs
  const formatSelectOptions = (arr, allLabel) =>
    (arr || []).map((item) => ({
      value: item,
      label: item === 'All' ? allLabel : item,
    }))

  return {
    state: {
      items: rows,
      rows,
      allRows: rows,
      totalCount,
      allFilteredCount: totalCount,
      summary,
      isLoading,
      error,
      searchField,
      searchText,
      companyFilter,
      branchFilter,
      courseFilter,
      batchFilter,
      deviceFilter,
      datePeriod,
      fromDate,
      toDate,
      orderBy,
      companyOptions: formatSelectOptions(options.companyOptions, 'All Companies'),
      branchOptions: formatSelectOptions(options.branchOptions, 'All Branches'),
      deviceOptions: formatSelectOptions(options.deviceOptions, 'All Devices'),
      courses: options.courses,
      batches: options.batches,
      datePeriodOptions: options.datePeriodOptions,
      searchFieldOptions: options.searchFieldOptions,
      currentPage,
      totalPages,
      pageSize,
      fromStudent,
      studentId,
    },
    actions: {
      setSearchField,
      setSearchText,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setBatchFilter,
      setDeviceFilter,
      setDatePeriod,
      handleDatePeriodChange,
      setFromDate,
      setToDate,
      setOrderBy,
      setCurrentPage,
      setPageSize: (v) => {
        setPageSize(Number(v))
        setCurrentPage(1)
      },
      search: () => {
        setCurrentPage(1)
        loadBiometricRecords()
      },
      resetFilters,
      goBack,
      refreshSummary: loadSummary,
    },
  }
}

export default useBiometricRecordsListScreen
